import React, { useMemo } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import dayjs from 'dayjs'
import { Alert, Button, Select, Space, Table, Tag, Typography } from 'antd'
import { EditOutlined, EyeOutlined } from '@ant-design/icons'

import ImportBasketSizeRatesButton from './ImportBasketSizeRatesButton'
import { BASKET_SIZE_RATES_QUERY, basketSizeDetailPath, formatPercent } from './graphql'

const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm'

const BasketSizeRatesTab = () => {
  const { t } = useTranslation('customerShippingRates')
  const history = useHistory()

  const { data, error, loading, refetch } = useQuery(BASKET_SIZE_RATES_QUERY, {
    fetchPolicy: 'cache-and-network'
  })

  const rows = (data && data.basketSizeRates) || []
  const permissions = (data && data.customerShippingRatePermissions) || {}

  const provincesWithoutTiers = useMemo(() => {
    const usedIds = new Set(rows.map(row => row.province.id))

    return ((data && data.provinces) || [])
      .filter(province => !usedIds.has(province.id))
      .sort((a, b) => a.name.localeCompare(b.name, 'th'))
  }, [data])

  const columns = [
    {
      title: t('province'),
      key: 'province',
      render: (_value, row) => <Link to={basketSizeDetailPath(row.province.id)}>{row.province.name}</Link>
    },
    {
      title: t('basketSize.tierCount'),
      key: 'tierCount',
      align: 'right',
      render: (_value, row) => row.tiers.length
    },
    {
      title: t('basketSize.catchAll'),
      dataIndex: 'hasCatchAll',
      key: 'hasCatchAll',
      render: value => (value
        ? <Tag color='green'>{t('basketSize.hasCatchAll')}</Tag>
        : <Tag color='orange'>{t('basketSize.missingCatchAll')}</Tag>)
    },
    {
      title: t('basketSize.fullTruckLoadPercentage'),
      dataIndex: 'fullTruckLoadPercentage',
      key: 'fullTruckLoadPercentage',
      align: 'right',
      render: formatPercent
    },
    {
      title: t('updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: value => (value ? dayjs(value).format(DATE_TIME_FORMAT) : '-')
    },
    {
      title: '',
      key: 'actions',
      render: (_value, row) => (
        <Button
          icon={row.canEdit ? <EditOutlined /> : <EyeOutlined />}
          onClick={() => history.push(basketSizeDetailPath(row.province.id))}
          size='small'
        >
          {row.canEdit ? t('edit') : t('view')}
        </Button>
      )
    }
  ]

  return (
    <React.Fragment>
      <Typography.Paragraph type='secondary'>{t('basketSize.description')}</Typography.Paragraph>

      {error && <Alert message={error.message} showIcon style={{ marginBottom: 16 }} type='error' />}

      {(permissions.canEditBasketSizeRates || permissions.canImportBasketSizeRates) && (
        <Space style={{ marginBottom: 16 }} wrap>
          {permissions.canEditBasketSizeRates && (
            <Select
              filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
              onChange={provinceId => history.push(basketSizeDetailPath(provinceId))}
              options={provincesWithoutTiers.map(province => ({ value: province.id, label: province.name }))}
              placeholder={t('basketSize.addProvince')}
              showSearch
              style={{ width: 260 }}
            />
          )}
          {permissions.canImportBasketSizeRates && <ImportBasketSizeRatesButton onEnqueued={() => refetch()} />}
        </Space>
      )}

      <Table
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={false}
        rowKey={row => row.province.id}
        scroll={{ x: 'max-content' }}
        size='small'
      />
    </React.Fragment>
  )
}

export default BasketSizeRatesTab
